import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faPlay,
  faPhone,
  faMobileAlt
} from '@fortawesome/free-solid-svg-icons';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { checkBPValues, dedupeMeasurementsByDateTimeKeepLatest, getMeasurementValueClass, getPresetDateRangeParams, formatDateDisplay } from '../../../utils/measurementUtils';
import measurementService from '../../../services/measurementService';
import patientService from '../../../services/patientService';
import AddBPForm from './AddBPForm';
import { toast } from 'react-toastify';
import VitalsTabs from '../../../components/MeasurementData/VitalsTabs/VitalsTabs';
import EmailModal from '../../../components/EmailModal/EmailModal';
import emailService from '../../../services/emailService';
import { printElement } from '../../../utils/reportExportUtils';
import { exportBloodPressureTablesPdf } from '../../../utils/measurementTablePdfExport';
import {
  findPeriodByName,
  normalizePeriodName,
  resolvePeriodNameByIdOrLabel,
  resolveDisplayScheduleTargets,
  hasAnyScheduleTargets,
  getMeasurementEpochMs
} from '../../../utils/scheduleTargetUtils';
import {
  PATIENT_SCHEDULE_TARGET_PREF_EVENT,
  getUsePatientScheduleTargetsAtMeasurementMs,
  getUsePatientScheduleTargetsPreference
} from '../../../utils/patientScheduleTargetPreference.js';
import AddFollowUpModal from '../../../components/AddFollowUpModal/AddFollowUpModal';
import pulseIcon from '../../../assets/pulse.png';
import PageLoadingSpinner from '../../../components/PageLoadingSpinner/PageLoadingSpinner';
import './BloodPressure.css';

const BloodPressure = ({ practiceId, patientId, practiceIsLocked = false }) => {
  const BP_PERMANENT_START_TIMES = {
    'Wake-up': '06:01 AM',
    Morning: '08:01 AM',
    Noon: '11:01 AM',
    Afternoon: '02:01 PM',
    Evening: '06:01 PM'
  };
  const { updateBreadcrumbs } = useBreadcrumb();
  const [timer, setTimer] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('Weekly average');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [scheduleTargetSources, setScheduleTargetSources] = useState(null);
  const [targetTimelineRev, setTargetTimelineRev] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(true);
  const [hasFetchedPatientData, setHasFetchedPatientData] = useState(false);
  const [hasFetchedScheduleTargets, setHasFetchedScheduleTargets] = useState(false);
  const [hasFetchedMeasurements, setHasFetchedMeasurements] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const initialized = useRef(false);
  const bpTimePeriods = useMemo(
    () => (Array.isArray(scheduleTargets?.bloodPressureTimePeriod) ? scheduleTargets.bloodPressureTimePeriod : []),
    [scheduleTargets]
  );
  const bpTargetRanges = useMemo(
    () => (Array.isArray(scheduleTargets?.bloodPressureTargetRange) ? scheduleTargets.bloodPressureTargetRange : []),
    [scheduleTargets]
  );

  const resolveTargetsBundleAtMs = useCallback(
    (ms) => {
      if (!scheduleTargetSources) return null;
      const usePatient = getUsePatientScheduleTargetsAtMeasurementMs(
        practiceId,
        patientId,
        ms,
        scheduleTargetSources.patientData
      );
      return resolveDisplayScheduleTargets({
        practiceData: scheduleTargetSources.practiceData,
        patientData: scheduleTargetSources.patientData,
        usePatientTargets: usePatient
      });
    },
    [scheduleTargetSources, practiceId, patientId, targetTimelineRev]
  );

  useEffect(() => {
    if (practiceIsLocked) {
      setShowAddModal(false);
      setShowFollowUpModal(false);
      setIsRunning(false);
      setTimer(0);
    }
  }, [practiceIsLocked]);

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setHasFetchedPatientData(false);
      setHasFetchedScheduleTargets(false);
      setHasFetchedMeasurements(false);
      setScheduleTargetSources(null);
      if (!initialized.current) {
        if (!practiceIsLocked) {
          setIsRunning(true);
        }
        initialized.current = true;
      }
      fetchPatientData();
      fetchScheduleTargets();
    }
    // Optional: Reset initialized if we want to restart timer when switching patients
    return () => {
      // If we want to keep the timer running across patient switches, don't reset here.
      // But usually unmounting means a fresh start.
      initialized.current = false;
    };
  }, [practiceId, patientId]);

  // Fetch measurements only on patient/screen load.
  // Query filters are applied explicitly via the Query button.
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements({ silent: true });
    }
  }, [practiceId, patientId]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (isRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning]);

  const fetchPatientData = async () => {
    const cacheKey = `measurement:patient:${practiceId}:${patientId}`;
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && typeof cached === 'object') {
          setPatientData(cached);
        }
      } catch {
        // ignore cache parse issues
      }
    }
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        const next = response.data.data;
        setPatientData(next);
        sessionStorage.setItem(cacheKey, JSON.stringify(next));
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
    } finally {
      setHasFetchedPatientData(true);
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const sources = await patientService.getScheduleTargetSourcesForPatient(practiceId, patientId);
      setScheduleTargetSources(sources);
      const usePatientTargets = getUsePatientScheduleTargetsPreference(practiceId, patientId, sources?.patientData);
      const effective = resolveDisplayScheduleTargets({
        practiceData: sources.practiceData,
        patientData: sources.patientData,
        usePatientTargets
      });
      setScheduleTargets(hasAnyScheduleTargets(effective) ? effective : sources.practiceData);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    } finally {
      setHasFetchedScheduleTargets(true);
    }
  };

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      setTargetTimelineRev((n) => n + 1);
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  const fetchMeasurements = async ({ silent = false, bypassCache = false } = {}) => {
    try {
      let fromDateParam, toDateParam;
      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else if (fromDate && toDate) {
        fromDateParam = fromDate;
        toDateParam = toDate;
      }
      const queryCacheKey = `measurement:bp:${practiceId}:${patientId}:${queryType}:${dateRange}:${fromDateParam || ''}:${toDateParam || ''}:${sortBy}`;
      let hadCachedData = false;
      if (!bypassCache) {
        const cachedRaw = sessionStorage.getItem(queryCacheKey);
        if (cachedRaw) {
          try {
            const cached = JSON.parse(cachedRaw);
            if (Array.isArray(cached)) {
              setMeasurements(cached);
              setHasFetchedMeasurements(true);
              hadCachedData = true;
            }
          } catch {
            // ignore cache parse issues
          }
        }
      }
      if (!silent || !hadCachedData) {
        setQueryLoading(true);
      }

      if (fromDateParam && toDateParam) {
        const response = await measurementService.getBloodPressureByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          sortBy.toLowerCase().replace(' ', '_')
        );

        if (response.data?.success) {
          const next = response.data.data || [];
          setMeasurements((prev) => (JSON.stringify(prev || []) === JSON.stringify(next) ? prev : next));
          sessionStorage.setItem(queryCacheKey, JSON.stringify(next));
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);

      // Handle 404 gracefully - API endpoint may not be implemented yet
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
        // Don't show error toast for 404 - endpoint may not be implemented yet
      } else {
        // Only show error for other types of errors
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
      setHasFetchedMeasurements(true);
    }
  };

  const handleQuery = () => {
    if (queryType === 'from' && (!fromDate || !toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const handleAddDataSuccess = () => {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(`measurement:bp:${practiceId}:${patientId}`)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // ignore
    }
    fetchMeasurements({ silent: false, bypassCache: true });
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => formatDateDisplay(dateString);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Handle both 24-hour and 12-hour formats
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    const parts = timeString.split(':');
    const h = parseInt(parts[0]);
    const m = parts[1] ? parts[1].slice(0, 2) : '00';
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHours = h % 12 || 12;
    return `${displayHours}:${m} ${period}`;
  };

  const resolvePeriodByTime = (timeString, periods) => {
    if (!timeString || !periods?.length) return '';
    
    // Normalize timeString to 24h for comparison
    let h, m;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      const [time, ampm] = timeString.trim().split(/\s+/);
      const [hh, mm] = time.split(':').map(Number);
      h = hh;
      if (ampm?.toUpperCase() === 'PM' && h !== 12) h += 12;
      if (ampm?.toUpperCase() === 'AM' && h === 12) h = 0;
      m = mm || 0;
    } else {
      const parts = timeString.split(':').map(Number);
      h = parts[0] || 0;
      m = parts[1] || 0;
    }
    
    const cur = h * 60 + m;
    
    const convertToMinutes = (t) => {
      if (!t) return 0;
      if (t.includes('AM') || t.includes('PM')) {
        const [time, ampm] = t.trim().split(/\s+/);
        const [hh, mm] = time.split(':').map(Number);
        let hour = hh;
        if (ampm?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
        if (ampm?.toUpperCase() === 'AM' && hour === 12) hour = 0;
        return hour * 60 + (mm || 0);
      }
      const [hh, mm] = t.split(':').map(Number);
      return (hh || 0) * 60 + (mm || 0);
    };

    for (const p of periods) {
      const start = convertToMinutes(p.startTime || p.start);
      let end = convertToMinutes(p.endTime || p.end);
      if (end < start) end += 1440;
      
      if (cur >= start && cur < end) return p.name;
      if (cur + 1440 >= start && cur + 1440 < end) return p.name;
    }
    return '';
  };

  // Get patient info
  const patient = patientData?.patient;
  const patientInfo = useMemo(() => {
    const patientName = patient ? `${patient.last_name || ''}, ${patient.first_name || ''}`.trim() : 'Loading...';
    const dobRaw = patient?.date_of_birth || patient?.dob || patient?.patient_birthday || '';
    const dobDate = dobRaw ? new Date(dobRaw) : null;
    const hasValidDob = dobDate && !Number.isNaN(dobDate.getTime());
    const patientDOB = hasValidDob
      ? `${formatDate(dobRaw)} (${new Date().getFullYear() - dobDate.getFullYear()} years)`
      : 'N/A';
    const patientPhone = patient?.phone_number || 'N/A';
    const patientDevice = patientData?.device_serial || 'N/A';
    return { patientName, patientDOB, patientPhone, patientDevice };
  }, [patient, patientData?.device_serial]);
  const { patientName, patientDOB, patientPhone, patientDevice } = patientInfo;
  const patientVitals = patientData?.patient?.vitals || 'BP, BG, WT';

  // Set breadcrumbs (must be after patientName is declared)
  useEffect(() => {
    if (patientData) {
      const resolvedPracticeName =
        patientData?.patient?.practice_name ||
        patientData?.practice?.practice_name ||
        patientData?.practice_name ||
        '';
      const practiceLabel = String(resolvedPracticeName).trim() || 'Practice';
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices', active: false },
        { label: practiceLabel, path: `/practices/${practiceId}`, active: false },
        { label: 'Patients', path: `/practices/${practiceId}?tab=patient`, active: false },
        { label: patientName, path: `/practices/${practiceId}/patients/${patientId}`, active: false },
        { label: 'Summary Of Measurement', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, patientName, updateBreadcrumbs]);

  // Get period names from schedule targets
  const periodNames = useMemo(() => {
    if (bpTimePeriods.length === 0) return [];
    const seen = new Set();
    return bpTimePeriods
      .map((p) => p?.name)
      .filter(Boolean)
      .filter((name) => {
        const key = normalizePeriodName(name);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [bpTimePeriods]);

  const getPeriodColor = () => '#DEF3F5';

  // Get period info (including time ranges) from schedule targets
  const getPeriodInfo = (periodName) => {
    if (bpTimePeriods.length === 0) {
      return null;
    }

    const period = findPeriodByName(bpTimePeriods, periodName);

    if (!period) return null;

    const fixedStartTime = BP_PERMANENT_START_TIMES[period.name] || BP_PERMANENT_START_TIMES[periodName] || '';
    const startTime = fixedStartTime || period.startTime || period.start || '';
    const endTime = period.endTime || period.end || '';

    // Format time for display
    const formatTimeDisplay = (time) => {
      if (!time) return '';
      if (time.includes('AM') || time.includes('PM')) return time;
      const [h, m] = time.split(':');
      const hour = parseInt(h);
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${m || '00'} ${period}`;
    };

    return {
      name: period.name,
      startTime: startTime,
      endTime: endTime,
      startTimeDisplay: formatTimeDisplay(startTime),
      endTimeDisplay: formatTimeDisplay(endTime)
    };
  };

  const getResolvedPeriodName = (measurement) => {
    const ms = getMeasurementEpochMs(measurement);
    const bundle = resolveTargetsBundleAtMs(ms) || scheduleTargets;
    const periodsForRow = Array.isArray(bundle?.bloodPressureTimePeriod) ? bundle.bloodPressureTimePeriod : bpTimePeriods;

    const rawLabel =
      measurement?.period_name ||
      measurement?.period ||
      measurement?.measure_note ||
      measurement?.note ||
      '';

    const fromConfigured = rawLabel
      ? resolvePeriodNameByIdOrLabel(periodsForRow, { ...measurement, period_name: rawLabel })
      : resolvePeriodNameByIdOrLabel(periodsForRow, measurement);
    if (fromConfigured) return fromConfigured;

    if (rawLabel) {
      const normalizedRaw = normalizePeriodName(rawLabel);
      const direct = periodNames.find((p) => normalizePeriodName(p) === normalizedRaw);
      if (direct) return direct;
      return rawLabel;
    }

    const mTime = measurement.measurement_time ||
      measurement.measure_new_date_time?.split('T')[1]?.slice(0, 8) ||
      measurement.measure_date_time?.split(' ')[1];

    if (mTime) {
      const timeResolved = resolvePeriodByTime(mTime, periodsForRow);
      if (timeResolved) return timeResolved;
    }

    return 'Unknown';
  };

  const DEFAULT_BP_TARGET = {
    systolicMin: 100,
    systolicMax: 140,
    diastolicMin: 60,
    diastolicMax: 90,
    pulseMin: 60,
    pulseMax: 100
  };

  // Get target ranges for each period (optional bundle for historical categorization)
  const getTargetForPeriod = (periodName, bundleOverride) => {
    const bundle = bundleOverride || scheduleTargets;
    const ranges = Array.isArray(bundle?.bloodPressureTargetRange) ? bundle.bloodPressureTargetRange : [];
    const periods = Array.isArray(bundle?.bloodPressureTimePeriod) ? bundle.bloodPressureTimePeriod : [];
    const pickFirstNumber = (...values) => {
      for (const value of values) {
        if (value === null || value === undefined || value === '') continue;
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
      return null;
    };

    // Find matching target by period name or use first, falling back to DEFAULT_BP_TARGET
    const normalizedInput = normalizePeriodName(periodName);
    const period = periods.find(
      (p) => normalizePeriodName(p.name) === normalizedInput
    );
    const target = (ranges.length > 0 ? (ranges.find((t) => {
      const targetName = normalizePeriodName(t.name);
      return (
        targetName === normalizedInput ||
        targetName.includes(normalizedInput) ||
        normalizedInput.includes(targetName) ||
        t.id === period?.id
      );
    }) || ranges[0]) : null) || DEFAULT_BP_TARGET;

    return {
      systolicMin: pickFirstNumber(target.systolicMin, target.sysMin, target.systolic_min, target.systolic_pressure_min) ?? DEFAULT_BP_TARGET.systolicMin,
      systolicMax: pickFirstNumber(target.systolicMax, target.sysMax, target.systolic_max, target.systolic_pressure_max) ?? DEFAULT_BP_TARGET.systolicMax,
      diastolicMin: pickFirstNumber(target.diastolicMin, target.diaMin, target.diastolic_min, target.diastolic_pressure_min) ?? DEFAULT_BP_TARGET.diastolicMin,
      diastolicMax: pickFirstNumber(target.diastolicMax, target.diaMax, target.diastolic_max, target.diastolic_pressure_max) ?? DEFAULT_BP_TARGET.diastolicMax,
      pulseMin: pickFirstNumber(target.pulseMin, target.pulse_min, target.minPulse, target.min_value) ?? DEFAULT_BP_TARGET.pulseMin,
      pulseMax: pickFirstNumber(target.pulseMax, target.pulse_max, target.maxPulse, target.max_value) ?? DEFAULT_BP_TARGET.pulseMax
    };
  };

  const checkPulseStatus = (pulseValue, target) => {
    if (pulseValue == null || Number.isNaN(pulseValue)) return null;
    const min = Number(target?.pulseMin ?? 60);
    const max = Number(target?.pulseMax ?? 100);
    if (pulseValue < min) return 'low';
    if (pulseValue > max) return 'high';
    return 'normal';
  };

  // Group measurements by date - keep ALL measurements
  const effectiveMeasurements = useMemo(
    () => dedupeMeasurementsByDateTimeKeepLatest(measurements),
    [measurements]
  );

  const awaitingTargetsForColoring = useMemo(
    () =>
      effectiveMeasurements.length > 0 &&
      (!hasFetchedScheduleTargets || scheduleTargetSources == null || !hasFetchedMeasurements),
    [
      effectiveMeasurements.length,
      hasFetchedScheduleTargets,
      scheduleTargetSources,
      hasFetchedMeasurements
    ]
  );

  // Group measurements by date
  const groupedMeasurements = useMemo(() => {
    const grouped = {};

    // Sort all measurements by date and time (newest first)
    const sortedMeasurements = [...effectiveMeasurements].sort((a, b) => {
      const dateA = a.measurement_date || a.measure_new_date_time?.split('T')[0] || a.created_at?.split('T')[0] || '';
      const dateB = b.measurement_date || b.measure_new_date_time?.split('T')[0] || b.created_at?.split('T')[0] || '';

      if (dateA !== dateB) {
        return new Date(dateB) - new Date(dateA); // Descending by date
      }

      // Same date, sort by time
      const timeA = a.measurement_time ||
        a.measure_new_date_time?.split('T')[1] ||
        a.measure_date_time?.split(' ')[1] ||
        a.created_at?.split('T')[1] ||
        '00:00:00';
      const timeB = b.measurement_time ||
        b.measure_new_date_time?.split('T')[1] ||
        b.measure_date_time?.split(' ')[1] ||
        b.created_at?.split('T')[1] ||
        '00:00:00';
      return timeB.localeCompare(timeA); // Descending by time
    });

    // 3. Group by date - each measurement is stored in a flat array per date
    sortedMeasurements.forEach(measurement => {
      const date = measurement.measurement_date || measurement.measure_new_date_time?.split('T')[0] || measurement.created_at?.split('T')[0];

      if (!grouped[date]) {
        grouped[date] = [];
      }

      grouped[date].push(measurement);
    });

    return grouped;
  }, [effectiveMeasurements]);

  // Get all unique dates sorted
  const sortedDates = useMemo(() => {
    return Object.keys(groupedMeasurements).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedMeasurements]);

  const groupedByWeek = useMemo(() => {
    if (measurements.length === 0) return [];
    
    const weeksMap = {};
    measurements.forEach(m => {
      const dateStr = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0];
      if (!dateStr) return;
      
      const d = new Date(dateStr);
      // Start of week (Sunday)
      const sun = new Date(d);
      sun.setDate(d.getDate() - d.getDay());
      sun.setHours(0, 0, 0, 0);
      const weekKey = sun.toISOString().split('T')[0];
      
      if (!weeksMap[weekKey]) {
        const sat = new Date(sun);
        sat.setDate(sun.getDate() + 6);
        
        weeksMap[weekKey] = {
          key: weekKey,
          start: sun,
          end: sat,
          label: `${formatDate(sun)} ~ ${formatDate(sat)}`,
          dates: {},
          allMeasurements: []
        };
      }
      
      if (!weeksMap[weekKey].dates[dateStr]) {
        weeksMap[weekKey].dates[dateStr] = [];
      }
      weeksMap[weekKey].dates[dateStr].push(m);
      weeksMap[weekKey].allMeasurements.push(m);
    });

    return Object.keys(weeksMap)
      .sort((a, b) => new Date(b) - new Date(a))
      .map(key => {
        const week = weeksMap[key];
        return {
          ...week,
          sortedDates: Object.keys(week.dates).sort((a, b) => new Date(b) - new Date(a))
        };
      });
  }, [measurements]);

  // Calculate statistics for a period - uses ALL measurements across all dates
  const calculatePeriodStats = (allMeasurements, periodName) => {
    // Filter measurements for this specific period
    const normalizedPeriod = normalizePeriodName(periodName);
    const periodMeasurements = allMeasurements.filter(m => {
      const period = getResolvedPeriodName(m);
      return normalizePeriodName(period) === normalizedPeriod;
    });

    if (!periodMeasurements || periodMeasurements.length === 0) {
      return { avg: null, max: null, min: null, count: 0, above: 0, below: 0, within: 0 };
    }

    const sysValues = periodMeasurements.map(m => parseFloat(m.systolic_pressure)).filter(v => !isNaN(v));
    const diaValues = periodMeasurements.map(m => parseFloat(m.diastolic_pressure)).filter(v => !isNaN(v));
    const pulseValues = periodMeasurements.map(m => {
      const pulse = m.pulse;
      // Ensure pulse is a number, not a string
      if (pulse == null || pulse === undefined) return null;
      return typeof pulse === 'string' ? parseFloat(pulse) : pulse;
    }).filter(v => v != null && !isNaN(v));
    const sysAvg = sysValues.length > 0 ? sysValues.reduce((a, b) => a + b, 0) / sysValues.length : null;
    const diaAvg = diaValues.length > 0 ? diaValues.reduce((a, b) => a + b, 0) / diaValues.length : null;
    const pulseAvg = pulseValues.length > 0 ? pulseValues.reduce((a, b) => a + b, 0) / pulseValues.length : null;
    const sysMax = sysValues.length > 0 ? Math.max(...sysValues) : null;
    const diaMax = diaValues.length > 0 ? Math.max(...diaValues) : null;
    const pulseMax = pulseValues.length > 0 ? Math.max(...pulseValues) : null;
    const sysMin = sysValues.length > 0 ? Math.min(...sysValues) : null;
    const diaMin = diaValues.length > 0 ? Math.min(...diaValues) : null;
    const pulseMin = pulseValues.length > 0 ? Math.min(...pulseValues) : null;

    let above = 0, below = 0, within = 0;
    periodMeasurements.forEach(m => {
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(periodName, bundle);
      const { sysStatus, diaStatus } = checkBPValues(m, target);
      if (sysStatus === 'high' || diaStatus === 'high') above++;
      else if (sysStatus === 'low' || diaStatus === 'low') below++;
      else within++;
    });

    return {
      avg: { sys: sysAvg, dia: diaAvg, pulse: pulseAvg },
      max: { sys: sysMax, dia: diaMax, pulse: pulseMax },
      min: { sys: sysMin, dia: diaMin, pulse: pulseMin },
      count: periodMeasurements.length,
      above,
      below,
      within
    };
  };

  // Render all readings for one period/date inside the same table cells
  const renderPeriodMeasurementsCells = (periodMeasurements, periodName) => {
    if (!periodMeasurements || periodMeasurements.length === 0) {
      return (
        <>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </>
      );
    }

    const renderPulseWithIcon = (value, status) => (
      value || value === 0 ? (
        <span className={`bp-pulse-with-icon ${status === 'high' ? 'bp-value-high' : status === 'low' ? 'bp-value-low' : 'bp-value-normal'}`}>
          {Math.round(value)}
          {(Number(value) < 60 || Number(value) > 100) && (
            <img src={pulseIcon} alt="pulse" className="bp-pulse-icon" />
          )}
        </span>
      ) : ''
    );

    const readings = [...periodMeasurements].sort((a, b) => {
      const timeA = a.measurement_time ||
        a.measure_new_date_time?.split('T')[1] ||
        a.measure_date_time?.split(' ')[1] ||
        a.created_at?.split('T')[1] ||
        '00:00:00';
      const timeB = b.measurement_time ||
        b.measure_new_date_time?.split('T')[1] ||
        b.measure_date_time?.split(' ')[1] ||
        b.created_at?.split('T')[1] ||
        '00:00:00';
      return timeB.localeCompare(timeA);
    });

    return (
      <>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getTargetForPeriod(periodName, bundle);
              const { sysStatus, diaStatus } = checkBPValues(measurement, target);
              let cellClass = 'bp-value-normal';
              if (sysStatus === 'high') cellClass = 'bp-value-high';
              else if (sysStatus === 'low') cellClass = 'bp-value-low';
              return (
                <div key={`${measurement.id || index}-sys`} className={cellClass}>
                  {measurement.systolic_pressure ? Math.round(measurement.systolic_pressure) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getTargetForPeriod(periodName, bundle);
              const { sysStatus, diaStatus } = checkBPValues(measurement, target);
              let cellClass = 'bp-value-normal';
              if (diaStatus === 'high') cellClass = 'bp-value-high';
              else if (diaStatus === 'low') cellClass = 'bp-value-low';
              return (
                <div key={`${measurement.id || index}-dia`} className={cellClass}>
                  {measurement.diastolic_pressure ? Math.round(measurement.diastolic_pressure) : ''}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const bundle =
                resolveTargetsBundleAtMs(getMeasurementEpochMs(measurement)) || scheduleTargets;
              const target = getTargetForPeriod(periodName, bundle);
              const pulseValue = measurement.pulse
                ? (typeof measurement.pulse === 'string' ? parseFloat(measurement.pulse) : measurement.pulse)
                : null;
              const pulseStatus = checkPulseStatus(pulseValue, target);
              return (
                <div key={`${measurement.id || index}-pulse`}>
                  {renderPulseWithIcon(pulseValue, pulseStatus)}
                </div>
              );
            })}
          </div>
        </td>
        <td>
          <div className="bp-cell-stack">
            {readings.map((measurement, index) => {
              const measureTime = measurement.measurement_time ||
                measurement.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                measurement.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                '';
              const formattedTime = measureTime ? formatTime(measureTime) : '';
              return <div key={`${measurement.id || index}-time`} className="bp-time-text">{formattedTime}</div>;
            })}
          </div>
        </td>
      </>
    );
  };

  // Calculate totals across all periods - uses UNIQUE measurements
  const totals = useMemo(() => {
    let totalAboveSys = 0, totalAboveDia = 0;
    let totalBelowSys = 0, totalBelowDia = 0;
    let totalWithinSys = 0, totalWithinDia = 0;

    effectiveMeasurements.forEach(m => {
      const period = getResolvedPeriodName(m);
      const bundle = resolveTargetsBundleAtMs(getMeasurementEpochMs(m)) || scheduleTargets;
      const target = getTargetForPeriod(period, bundle);
      const { sysStatus, diaStatus } = checkBPValues(m, target);
      
      const sysVal = m.systolic_pressure;
      const diaVal = m.diastolic_pressure;

      if (sysVal != null && sysVal !== '') {
        if (sysStatus === 'high') totalAboveSys++;
        else if (sysStatus === 'low') totalBelowSys++;
        else totalWithinSys++;
      }

      if (diaVal != null && diaVal !== '') {
        if (diaStatus === 'high') totalAboveDia++;
        else if (diaStatus === 'low') totalBelowDia++;
        else totalWithinDia++;
      }
    });

    const sysTotal = totalAboveSys + totalBelowSys + totalWithinSys;
    const diaTotal = totalAboveDia + totalBelowDia + totalWithinDia;

    return {
      above: { sys: totalAboveSys, dia: totalAboveDia },
      below: { sys: totalBelowSys, dia: totalBelowDia },
      within: { sys: totalWithinSys, dia: totalWithinDia },
      sysTotal,
      diaTotal
    };
  }, [effectiveMeasurements, scheduleTargets, scheduleTargetSources, targetTimelineRev, practiceId, patientId]);

  const formatPercentCount = (count, total) => {
    if (!total) return `0% (${count})`;
    const pct = Math.round((count / total) * 100);
    return `${pct}% (${count})`;
  };

  const reportTitle = `Blood Pressure Report - ${patientName}`;
  const exportFilename = `BP-Report-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;

  const handlePrint = () => {
    try {
      const el = document.getElementById('bp-report-content');
      printElement(el, reportTitle);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const handleExport = () => {
    if (effectiveMeasurements.length === 0) {
      toast.warning('No data to export');
      return;
    }
    try {
      exportBloodPressureTablesPdf({
        filename: exportFilename,
        title: reportTitle,
        patient: {
          name: patientName,
          dob: patientDOB,
          phone: patientPhone,
          device: patientDevice
        },
        periodList:
          periodNames.length > 0
            ? periodNames
            : ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'],
        sortedDates,
        groupedMeasurements,
        getResolvedPeriodName,
        calculatePeriodStats,
        effectiveMeasurements,
        totals,
        formatPercentCount,
        formatDate,
        formatTime
      });
      toast.success('Exported to PDF');
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    }
  };

  const defaultEmailSubject = `Blood Pressure Report - ${patientName} (${new Date().toLocaleDateString()})`;
  const defaultEmailContent = `Blood Pressure Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Readings: ${effectiveMeasurements.length}`;

  return (
    <div className="blood-pressure-page">
      {practiceIsLocked && (
        <div className="practice-locked-measurement-banner" role="status">
          This practice is locked. Historical readings remain available for up to 30 days. New measurements and
          follow-ups are not allowed.
        </div>
      )}
      <div id="bp-report-content">
      <div className="bp-layout-grid">
        <h4 className="bp-layout-title" data-export-ignore="true">BP Measurement Controls</h4>
        <div className="bp-layout-top-row">
          {!practiceIsLocked && (
            <div className="bp-timer-wrapper" data-export-ignore="true">
              <div className="bp-timer-box">
                <span className="bp-timer-value">{formatTimer(timer)}</span>
              </div>
              <button
                type="button"
                className="bp-timer-pause-btn"
                onClick={() => setIsRunning(prev => !prev)}
                title={isRunning ? "Pause Timer" : "Start Timer"}
              >
                {isRunning ? (
                  <div className="bp-pause-icon"><span>|</span><span>|</span></div>
                ) : (
                  <FontAwesomeIcon icon={faPlay} className="bp-play-icon" />
                )}
              </button>
            </div>
          )}

          <div className="bp-patient-details-grid">
            <div className="bp-info-item">
              <span className="bp-label">Name :</span>
              <span className="bp-value">{patientName}</span>
            </div>
            <div className="bp-info-item">
              <span className="bp-label">DOB :</span>
              <span className="bp-value">{patientDOB}</span>
            </div>
            <div className="bp-info-item">
              <span className="bp-label">Phone :</span>
              <span className="bp-value">{patientPhone}</span>
            </div>
            <div className="bp-info-item">
              <span className="bp-label">Device :</span>
              <span className="bp-value">{patientDevice}</span>
            </div>
          </div>
        </div>

        <div className="bp-layout-middle-row" data-export-ignore="true">
          <div className="bp-middle-left">
            <VitalsTabs
              practiceId={practiceId}
              patientId={patientId}
              vitals={patientVitals}
              active="blood-pressure"
              mode="measurement"
            />
          </div>
          {!practiceIsLocked && (
            <div className="bp-middle-right">
              <button className="bp-add-followup-btn" type="button" onClick={() => setShowFollowUpModal(true)}>
                Add Follow Up
              </button>
            </div>
          )}
        </div>
      </div>

        <>
        {/* Query Section */}
        <div className="bp-query-box-custom" data-export-ignore="true">
          <h4 className="bp-query-title-custom">Query Period</h4>
          <div className="bp-query-body">
            <div className="bp-query-left">
              <div className="bp-radio-row bp-query-row-first">
                <label className="bp-radio-label">
                  <input
                    type="radio"
                    name="queryType"
                    value="dateRange"
                    checked={queryType === 'dateRange'}
                    onChange={(e) => setQueryType(e.target.value)}
                  />
                  <span style={{ fontWeight: 600, minWidth: '80px' }}>Date Range</span>
                </label>
                <select
                  className="bp-dropdown"
                  style={{ marginLeft: '20px', flex: 1 }}
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  disabled={queryType !== 'dateRange'}
                >
                  <option>Last 30 days</option>
                  <option>Last 7 days</option>
                  <option>Last 14 days</option>
                  <option>Last 60 days</option>
                  <option>Last 90 days</option>
                </select>
              </div>

              <div className="bp-radio-row" style={{ marginTop: '20px' }}>
                <label className="bp-radio-label">
                  <input
                    type="radio"
                    name="queryType"
                    value="from"
                    checked={queryType === 'from'}
                    onChange={(e) => setQueryType(e.target.value)}
                  />
                  <span style={{ fontWeight: 600, minWidth: '80px' }}>From</span>
                </label>
                <div className="bp-date-inputs" style={{ marginLeft: '20px', flex: 1 }}>
                  <input
                    type="date"
                    className="bp-date-input"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    disabled={queryType !== 'from'}
                  />
                  <span className="bp-date-separator" style={{ margin: '0 15px', fontWeight: 600 }}>To</span>
                  <input
                    type="date"
                    className="bp-date-input"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                    disabled={queryType !== 'from'}
                  />
                </div>
              </div>
            </div>

            <div className="bp-query-right" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div className="bp-radio-row bp-query-row-first">
                <div style={{ fontWeight: 600, minWidth: '85px' }}>Sort Data by</div>
                <select
                  className="bp-dropdown"
                  style={{ marginLeft: '20px', flex: 1 }}
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option>Weekly average</option>
                  <option>Daily average</option>
                  <option>Monthly average</option>
                  <option>Date</option>
                </select>
              </div>

              <div className="bp-action-buttons-row">
                <button className="bp-action-btn" onClick={handleQuery}>
                  Query
                </button>
                {!practiceIsLocked && (
                  <button className="bp-action-btn" onClick={() => setShowAddModal(true)}>Add Data</button>
                )}
                <button className="bp-action-btn" onClick={handlePrint}>Print</button>
                <button className="bp-action-btn" onClick={handleExport}>Export</button>
                <button className="bp-action-btn" onClick={() => setShowEmailModal(true)}>Email</button>
              </div>
            </div>
          </div>
        </div>

        <div className="bp-outline-section">
          <h4 className="bp-outline-title">Blood Pressure</h4>
          
          {queryLoading || awaitingTargetsForColoring ? (
            <PageLoadingSpinner minHeight="280px" />
          ) : loading ? (
            <PageLoadingSpinner minHeight="200px" />
          ) : effectiveMeasurements.length === 0 ? (
            <div className="bp-no-data-message">No measurement data available for the selected period.</div>
          ) : null}

          {!queryLoading && !awaitingTargetsForColoring && (
          <div className="bp-measurement-table-section bp-section-table">
            <table className="bp-measurement-table">
            <thead>
              <tr>
                <th rowSpan="2" className="bp-table-label-cell">
                  <span className="bp-measurement-date-header-label">Measurement</span>
                  <br />
                  <span className="bp-measurement-date-header-label">Date</span>
                </th>
                {periodNames.length > 0 ? (
                  periodNames.map((periodName, periodIndex) => {
                    const periodInfo = getPeriodInfo(periodName);
                    const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                      ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                      : '';
                    return (
                      <th key={periodName} colSpan="4" className="bp-table-time-header" style={{ backgroundColor: getPeriodColor(periodIndex) }}>
                        {periodName}
                        {timeRange && (
                          <>
                            <br /><span className="bp-time-text">{timeRange}</span>
                          </>
                        )}
                      </th>
                    );
                  })
                ) : (
                  <>
                    {['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'].map(periodName => {
                      const periodInfo = getPeriodInfo(periodName);
                      const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                        ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                        : '';
                      return (
                        <th key={periodName} colSpan="4" className="bp-table-time-header">
                          {periodName}
                          {timeRange && (
                            <>
                              <br /><span className="bp-time-text">{timeRange}</span>
                            </>
                          )}
                        </th>
                      );
                    })}
                  </>
                )}
              </tr>
              <tr>
                {periodNames.length > 0 ? (
                  periodNames.map(periodName => (
                    <React.Fragment key={periodName}>
                      <th className="bp-table-sub-header">Systolic</th>
                      <th className="bp-table-sub-header">Diastolic</th>
                      <th className="bp-table-sub-header">Pulse</th>
                      <th className="bp-table-sub-header">Measure Time</th>
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Measure Time</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Measure Time</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Measure Time</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Measure Time</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Measure Time</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Data rows */}
              {sortedDates.length > 0 ? (
                sortedDates.map(date => {
                  const dateMeasurements = groupedMeasurements[date] || [];
                  const periodList = periodNames.length > 0 ? periodNames : ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];

                  // Group measurements for this date by period to calculate averages
                  const dateMeasurementsByPeriod = {};
                  dateMeasurements.forEach(m => {
                    const resolvedPeriod = getResolvedPeriodName(m);
                    const matchedPeriod = periodList.find(
                      (p) => normalizePeriodName(p) === normalizePeriodName(resolvedPeriod)
                    );
                    if (!matchedPeriod) return;
                    if (!dateMeasurementsByPeriod[matchedPeriod]) {
                      dateMeasurementsByPeriod[matchedPeriod] = [];
                    }
                    dateMeasurementsByPeriod[matchedPeriod].push(m);
                  });

                  return (
                    <React.Fragment key={date}>
                      <tr>
                        <td className="bp-table-label-cell bp-measurement-date-cell">
                          {formatDate(date)}
                        </td>
                        {periodList.map((periodName) => renderPeriodMeasurementsCells(dateMeasurementsByPeriod[periodName] || [], periodName))}
                      </tr>
                      {/* Average Row for this date */}
                      <tr className="bp-average-row">
                        <td className="bp-table-label-cell">Average</td>
                        {periodList.map(periodName => {
                          const periodMeasurements = dateMeasurementsByPeriod[periodName] || [];

                          if (periodMeasurements.length > 0) {
                            const sysValues = periodMeasurements.map(m => parseFloat(m.systolic_pressure)).filter(v => !isNaN(v));
                            const diaValues = periodMeasurements.map(m => parseFloat(m.diastolic_pressure)).filter(v => !isNaN(v));
                            const pulseValues = periodMeasurements.map(m => {
                              const pulse = m.pulse;
                              if (pulse == null || pulse === undefined) return null;
                              return typeof pulse === 'string' ? parseFloat(pulse) : pulse;
                            }).filter(v => v != null && !isNaN(v));

                            const formatAvgValue = (values) => {
                              if (values.length === 0) return '';
                              const avg = values.reduce((a, b) => a + b, 0) / values.length;
                              return Number.isInteger(avg) ? avg : avg.toFixed(2);
                            };

                            return (
                              <React.Fragment key={periodName}>
                                <td style={{ fontWeight: 600 }}>{formatAvgValue(sysValues)}</td>
                                <td style={{ fontWeight: 600 }}>{formatAvgValue(diaValues)}</td>
                                <td style={{ fontWeight: 600 }}>{formatAvgValue(pulseValues)}</td>
                                <td></td>
                              </React.Fragment>
                            );
                          }
                          return (
                            <React.Fragment key={periodName}>
                              <td></td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={periodNames.length > 0 ? periodNames.length * 4 + 1 : 21} className="bp-table-label-cell" style={{ textAlign: 'center', padding: '20px' }}>
                    No measurement data available
                  </td>
                </tr>
              )}
            </tbody>
            </table>
          </div>
          )}
        </div>

        <div className="bp-outline-section bp-summary-section">
          <h4 className="bp-outline-title">Summary Of Measurement</h4>
          {queryLoading || awaitingTargetsForColoring ? (
            <PageLoadingSpinner minHeight="240px" />
          ) : (
          <div className="bp-measurement-table-section bp-section-table">
            <table className="bp-measurement-table">
            <thead>
              <tr>
                <th rowSpan="2" className="bp-table-label-cell">Summary Statistics</th>
                {periodNames.length > 0 ? (
                  periodNames.map((periodName, periodIndex) => {
                    const periodInfo = getPeriodInfo(periodName);
                    const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                      ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                      : '';
                    return (
                      <th key={periodName} colSpan="3" className="bp-table-time-header" style={{ backgroundColor: getPeriodColor(periodIndex) }}>
                        {periodName}
                        {timeRange && (
                          <>
                            <br /><span className="bp-time-text">{timeRange}</span>
                          </>
                        )}
                      </th>
                    );
                  })
                ) : (
                  <>
                    {['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'].map(periodName => {
                      const periodInfo = getPeriodInfo(periodName);
                      const timeRange = periodInfo && periodInfo.startTimeDisplay && periodInfo.endTimeDisplay
                        ? `${periodInfo.startTimeDisplay} ~ ${periodInfo.endTimeDisplay}`
                        : '';
                      return (
                        <th key={periodName} colSpan="3" className="bp-table-time-header">
                          {periodName}
                          {timeRange && (
                            <>
                              <br /><span className="bp-time-text">{timeRange}</span>
                            </>
                          )}
                        </th>
                      );
                    })}
                  </>
                )}
              </tr>
              <tr>
                {periodNames.length > 0 ? (
                  periodNames.map(periodName => (
                    <React.Fragment key={periodName}>
                      <th className="bp-table-sub-header">Systolic</th>
                      <th className="bp-table-sub-header">Diastolic</th>
                      <th className="bp-table-sub-header">Pulse</th>
                    </React.Fragment>
                  ))
                ) : (
                  <>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                    <th className="bp-table-sub-header">Systolic</th>
                    <th className="bp-table-sub-header">Diastolic</th>
                    <th className="bp-table-sub-header">Pulse</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {/* Statistics Rows */}
              {['Average', 'Maximum', 'Minimum', 'Count', 'Above Target', 'Below Target', 'Within Target'].map(statType => (
                <tr key={statType}>
                  <td className="bp-table-label-cell">{statType}</td>
                  {(periodNames.length > 0 ? periodNames : ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening']).map(periodName => {
                    // Use ALL measurements for summary statistics
                    const stats = calculatePeriodStats(effectiveMeasurements, periodName);

                    if (statType === 'Average') {
                      return (
                        <React.Fragment key={periodName}>
                          <td>{stats.avg?.sys ? Math.round(stats.avg.sys) : ''}</td>
                          <td>{stats.avg?.dia ? Math.round(stats.avg.dia) : ''}</td>
                          <td>{stats.avg?.pulse ? Math.round(stats.avg.pulse) : ''}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Maximum') {
                      return (
                        <React.Fragment key={periodName}>
                          <td>{stats.max?.sys ? Math.round(stats.max.sys) : ''}</td>
                          <td>{stats.max?.dia ? Math.round(stats.max.dia) : ''}</td>
                          <td>{stats.max?.pulse ? Math.round(stats.max.pulse) : ''}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Minimum') {
                      return (
                        <React.Fragment key={periodName}>
                          <td>{stats.min?.sys ? Math.round(stats.min.sys) : ''}</td>
                          <td>{stats.min?.dia ? Math.round(stats.min.dia) : ''}</td>
                          <td>{stats.min?.pulse ? Math.round(stats.min.pulse) : ''}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Count') {
                      return (
                        <React.Fragment key={periodName}>
                          <td colSpan="3">{stats.count}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Above Target') {
                      return (
                        <React.Fragment key={periodName}>
                          <td colSpan="3">{stats.above}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Below Target') {
                      return (
                        <React.Fragment key={periodName}>
                          <td colSpan="3">{stats.below}</td>
                        </React.Fragment>
                      );
                    } else if (statType === 'Within Target') {
                      return (
                        <React.Fragment key={periodName}>
                          <td colSpan="3">{stats.within}</td>
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                </tr>
              ))}

              {/* Total Row - Dynamic Column Spans */}
              <tr className="bp-total-row">
                <td className="bp-table-label-cell bp-total-label">Total</td>
                <td colSpan="5" className="bp-total-cell">
                  <div className="bp-total-title">Above target</div>
                  <div>Systolic pressure : {formatPercentCount(totals.above.sys, totals.sysTotal)}</div>
                  <div>Diastolic pressure : {formatPercentCount(totals.above.dia, totals.diaTotal)}</div>
                </td>
                <td colSpan="5" className="bp-total-cell">
                  <div className="bp-total-title">Within target</div>
                  <div>Systolic pressure : {formatPercentCount(totals.within.sys, totals.sysTotal)}</div>
                  <div>Diastolic pressure : {formatPercentCount(totals.within.dia, totals.diaTotal)}</div>
                </td>
                <td colSpan="5" className="bp-total-cell">
                  <div className="bp-total-title">Below target</div>
                  <div>Systolic pressure : {formatPercentCount(totals.below.sys, totals.sysTotal)}</div>
                  <div>Diastolic pressure : {formatPercentCount(totals.below.dia, totals.diaTotal)}</div>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Add Measurement Modal */}
        {!practiceIsLocked && showAddModal && (
          <AddBPForm
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            practiceId={practiceId}
            patientId={patientId}
            initialScheduleTargets={scheduleTargets}
            onSuccess={handleAddDataSuccess}
            modalClassName="bp-add-data-modal"
          />
        )}

        {!practiceIsLocked && showFollowUpModal && (
          <AddFollowUpModal
            isOpen={showFollowUpModal}
            onClose={() => setShowFollowUpModal(false)}
            practiceId={practiceId}
            patientId={patientId}
            patientName={patientName}
            initialTimerValue={timer}
            onSuccess={() => { }}
          />
        )}
        </>
      </div>{/* end bp-report-content */}

      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        defaultSubject={defaultEmailSubject}
        defaultContent={defaultEmailContent}
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info('Attachments are not supported yet. Sending without attachments.');
            }
            setEmailSending(true);
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
              event_id: 9
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          } finally {
            setEmailSending(false);
          }
        }}
      />
    </div>
  );
};

export default BloodPressure;

